// ---------------------------------------------------------------------------
// Tier 1 — required tests (sandbox checks, not publish gates). Run INSIDE
// the sandbox harness against the rule's validation scenarios.
// ---------------------------------------------------------------------------

import type { AgenticLogicBoundedQueryStrategyReport } from "../sdk/scan-strategy";

/**
 * The canonical required-test checks, in result order. Wire format —
 * append-only. Nine keys, and NOT CSP's seven: item-level shape lives inside
 * the result envelope schema, and unique-item-id validation is folded into
 * `valid_envelope` (which delegates to `checkEnvelopeConformance` — the one
 * implementation of the cross-field rules the unrefined base schemas cannot
 * express, e.g. children only on bundle items — and additionally re-checks
 * them over the authored validation scenarios).
 *
 * `shipping_claims_gated` is the sixth and is RETIRED: it policed the copy in
 * an item's `reason`, and `reason` no longer exists. It is dispatched by no
 * check and can never appear in a fresh publish job. The key STAYS in this
 * list because the list is wire format — historical publish-job `detailsJson`
 * rows carry it, and dropping it would render those readouts as an unlabeled
 * raw string. Retired, not removed.
 *
 * `produces_output` is the seventh and the only one that judges whether the
 * rule can do its job AT ALL: a rule that returned nothing under every
 * scenario, fired no chain branch, and issued no catalog read is inert by
 * construction and cannot become non-inert at serving time.
 *
 * `renderable_on_surface` is the eighth, and it is the only one that judges the
 * rule's output against WHERE the rule will be placed: an envelope whose every
 * item is a kind no targeted surface can draw
 * ({@link AGENTIC_LOGIC_SURFACE_RENDERABLE_KINDS}) renders a permanently blank
 * placement. Distinct from `produces_output`, which asks whether the rule is
 * INERT — an unrenderable envelope satisfies it, because the rule did emit
 * items, read the catalog and fired a branch. Inert vs. UNDRAWABLE are
 * different failures with different remedies, so they are different keys.
 *
 * `intent_supported_on_surface` is the ninth, and it judges what the rule's
 * items ask the surface to DO rather than what they ask it to draw
 * ({@link AGENTIC_LOGIC_SURFACE_SUPPORTED_INTENTS}). A separate key from
 * `renderable_on_surface` because the failures are not the same size: an
 * unrenderable kind draws NOTHING — a blank placement, survivable, and a
 * mixed envelope still renders its renderable half — whereas an unsupported
 * intent draws the WRONG THING, an Add where a swap was authored, which
 * performs the opposite of the authored action on the shopper's cart. That is
 * why this check's predicate is PER ITEM (any item asking for an unsupported
 * intent fails) where the kinds check is whole-envelope (only an entirely
 * undrawable envelope fails).
 */
export const AgenticLogicRequiredTestKeyList = [
  "valid_envelope",
  "runtime_under_budget",
  "stable_output",
  "no_leaked_secrets",
  "fallback_safe_failure",
  // RETIRED — see the note above. Never dispatched; kept for historical rows.
  "shipping_claims_gated",
  "produces_output",
  "renderable_on_surface",
  "intent_supported_on_surface",
] as const;

export type AgenticLogicRequiredTestKey =
  (typeof AgenticLogicRequiredTestKeyList)[number];

export interface AgenticLogicRequiredTestResult {
  key: AgenticLogicRequiredTestKey;
  passed: boolean;
  detail?: string;
}

/** Console output captured while one scenario's invocations ran. */
export interface AgenticLogicRequiredTestConsoleGroup {
  scenarioName: string;
  output: string;
}

/**
 * SDK diagnostics emitted while one scenario's invocations ran — a chain
 * branch that fell through with its error message, a scan that stopped before
 * exhausting the catalogue, a stubbed accessor.
 *
 * Reported beside the console output because they are a different kind of
 * evidence: the rule chose to write its console output, the platform chose to
 * write these, and for a rule that returned an empty envelope without
 * throwing they are usually the only account of why.
 */
export interface AgenticLogicRequiredTestWarningGroup {
  scenarioName: string;
  warnings: string[];
}

/**
 * How full one scenario's placement came out, beside the reads that filled it.
 *
 * The under-fill half of the scan-cost width gate needs BOTH halves of the
 * same invocation: a bounded query that returned everything it asked for is
 * only evidence of a read sized too tight if the envelope it produced actually
 * came up short. Pairing an aggregate read report with an envelope from a
 * different run would compare two different reads, so the queries are carried
 * per scenario here rather than looked up in the aggregate.
 */
export interface AgenticLogicRuleFillObservation {
  scenarioName: string;
  slotsNeeded: number;
  itemCount: number;
  /** Bounded queries the SAME invocation issued. */
  boundedQueries: AgenticLogicBoundedQueryStrategyReport[];
}

export interface AgenticLogicRequiredTestsRunResult {
  results: AgenticLogicRequiredTestResult[];
  consoleLogs: AgenticLogicRequiredTestConsoleGroup[];
  warnings: AgenticLogicRequiredTestWarningGroup[];
}
